import React, { useState, useContext, useMemo } from 'react'
import styled from '@emotion/styled'
import Button from './Button'
import IconInline from './IconInline'
import UserContext from './UserContext'

const StyledDiv = styled.div`
  background: #f5f6f7;
  border-top: 2px solid #e5e6e7;
  padding: 25px 15px;
  margin-top: 30px;
  text-align: center;

  @media (min-width: 640px) {
    padding: 25px 30px;
  }

  h4 {
    margin-top: 0;
    margin-bottom: 15px;
  }

  p {
    font-size: 14px;
    line-height: 1.3;
  }

  label {
    font-size: 12px;
    display: block;
    margin: 0 0 4px;
  }
`

const StyledTextarea = styled.textarea`
  display: block;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  width: 100%;
  padding: 10px;
  margin: 0 0 10px;
  font-family: 'Fira Sans', 'Lucida Grande', 'Trebuchet MS', sans-serif;
`

const windowExists = typeof window !== 'undefined'

const WasThisHelpful = () => {
  const [wasHelpful, setWasHelpful] = useState(undefined)
  const [commented, setCommented] = useState(false)
  const [rowId, setRowId] = useState(undefined)
  const [textareaValue, setTextareaValue] = useState('')
  const [user] = useContext(UserContext)

  const selectionMade = typeof wasHelpful !== 'undefined'

  const webhookUrl =
    'https://triggers.losant.com/webhooks/-7c_sdE0EN0Fq1ZJldxq0xIiHrC_fO58d25yLdAl'
  const webhookMethod = 'POST'
  const webhookHeaders = {
    'Content-Type': 'application/json',
  }

  let pathname
  if (windowExists) {
    pathname = window.location.pathname
  }

  const reqBody = useMemo(() => {
    return {
      pathname,
      userId: user?.id,
      userName: user ? `${user.firstName} ${user.lastName}` : undefined,
      userEmail: user?.email,
      isInOrg: user?.summary ? user?.summary.orgCount > 0 : undefined,
    }
  }, [pathname, user])

  const onTextareaChange = (e) => {
    setTextareaValue(e.target.value)
  }
  const handleSubmit = async (form) => {
    form.preventDefault()
    setCommented(true)
    try {
      await fetch(webhookUrl, {
        method: webhookMethod,
        headers: webhookHeaders,
        body: JSON.stringify({
          ...reqBody,
          rowId,
          wasHelpful,
          comment: textareaValue,
        }),
      })
    } catch (e) {
      console.log(e)
    }
  }

  const handleClick = async (bool) => {
    setWasHelpful(bool)
    try {
      const clickResponse = await fetch(webhookUrl, {
        method: webhookMethod,
        headers: webhookHeaders,
        body: JSON.stringify({ ...reqBody, wasHelpful: bool, rowId }),
      })
      if (!rowId) {
        const parsed = await clickResponse.json()
        setRowId(parsed.rowId)
      }
    } catch (e) {
      console.log(e)
    }
  }
  const onClickHelpful = () => {
    if (wasHelpful !== true) {
      // prevent spamming the button
      handleClick(true)
    }
  }
  const onClickNotHelpful = () => {
    if (wasHelpful !== false) {
      // prevent spamming the button
      handleClick(false)
    }
  }

  return (
    <StyledDiv>
      <h4>Was this page helpful?</h4>
      <div>
        <Button
          onClick={onClickHelpful}
          variant={wasHelpful === true ? 'success' : 'default'}
          className={wasHelpful === true ? 'active' : undefined}
        >
          <IconInline glyph="THUMBS_UP" />
          Yes
        </Button>
        <Button
          onClick={onClickNotHelpful}
          variant={wasHelpful === false ? 'danger' : 'default'}
          className={wasHelpful === false ? 'active' : undefined}
        >
          <IconInline glyph="THUMBS_DOWN" />
          No
        </Button>
      </div>
      {selectionMade && (
        <div style={{ marginTop: 20, textAlign: 'left' }}>
          {wasHelpful ? (
            <p>
              🎉{' '}
              <em>
                Huzzah! Thanks for letting us know. If you would like to tell us
                more, we'd love to hear it.
              </em>
            </p>
          ) : (
            <p>
              🙁{' '}
              <em>
                We're sorry this didn't answer your question. Please let us know
                how we can improve this page.
              </em>
            </p>
          )}
          {commented ? (
            <p>
              👍{' '}
              <em>
                Your comment has been submitted. Thank you again for your
                feedback.
              </em>
            </p>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* the label is associated with a form element even though the linter doesn't think so */}
              <label htmlFor="feedback">Comments</label>{/* eslint-disable-line */}
              <StyledTextarea
                autoFocus
                id="feedback"
                onChange={onTextareaChange}
                value={textareaValue}
              />
              <Button type="submit" variant="primary" disabled={!textareaValue}>
                Send Comments
              </Button>
            </form>
          )}
        </div>
      )}
      <hr />
      <p style={{ marginBottom: 0 }}>
        Still looking for help? You can also search the{' '}
        <a
          href="https://forums.app.wnology.io/"
          target="_blank"
          rel="noreferrer"
        >
          WEGnology Forums
        </a>{' '}
        or submit your question there.
      </p>
    </StyledDiv>
  )
}

export default WasThisHelpful
